import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import "./App.css";
import AppRouter from "./routs/AppRouter";
import useUserStore from "./stores/userStore";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useOnMessage from "./hooks/useOnMessage";
import useGenerateDeviceToken from "./hooks/useGenerateDeviceToken";
import { useAppStore } from "./stores/appStore";
import { ThemeProvider } from "@mui/material";
import { getTheme } from "./colors/theme";
import Loader from "./components/loader/loader";
import { getBrowserData } from "./axios/user";
import { useQuizeStore } from "./stores/quizeStore";
import { ErrorBoundary } from "./components/error-boundary/error-boundary";
import InternetStatusBanner from "./components/internet-status-banner/internet-status-banner";
import InstallAppBanner from "./components/install-app-banner/install-app-banner";
import useInternetConnection from "./hooks/useInternetConnection";
import GlobalTtsPlayer from "./components/chat/chat-bubble/independent-tts/global-tts-player";
import { ConnectionErrorModal } from "./components/connection-error-modal/connection-error-modal";
import { initMultiTabDetection } from "./utils/multiTabDetection";
import { DeviceConflictModal } from "./components/device-conflict-modal/device-conflict-modal";
import { useSessionStatusCheck } from "./hooks/useSessionStatusCheck";
import { endActiveSessions } from "./utils/endActiveSessions";
import { isAdminRoute } from "./utils/isAdminRoute";

function App() {
  const { fetchUser, fetchDomainTree, token, appVersion, user } = useUserStore();
  const [isLoading, setIsLoading] = useState(() => Boolean(token));
  const { requestAndSendToken } = useGenerateDeviceToken();
  const {
    fetchConfig,
    setDomainFocus,
    colorTheme,
    connectionError,
    setConnectionError,
    deviceConflictModalOpen,
    setDeviceConflictModalOpen,
  } = useAppStore();
  const { handleActivateDevice, handleCancel, isActivating } =
    useSessionStatusCheck();
  const { fetchPreassessment } = useQuizeStore();
  const theme = useMemo(() => getTheme(colorTheme), [colorTheme]);
  useOnMessage();
  useInternetConnection();

  const sessionUserId = user?.id || "";

  useEffect(() => {
    if (!token || !sessionUserId) return;

    // Skip device conflict detection for admin routes
    if (isAdminRoute()) {
      return;
    }

    const handleMultipleTabsDetected = async () => {
      await endActiveSessions();
      setDeviceConflictModalOpen(true);
      setConnectionError(null);
    };

    const handleSessionClaimed = () => {
      if (connectionError) {
        setConnectionError(null);
      }
    };

    const cleanup = initMultiTabDetection(
      sessionUserId,
      handleMultipleTabsDetected,
      handleSessionClaimed
    );

    return cleanup;
  }, [token, sessionUserId, setConnectionError, setDeviceConflictModalOpen]);

  //Close dialogs when user switches to another tab
  useEffect(() => {
    if (!token) return;

    // Skip for admin routes
    if (isAdminRoute()) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        await endActiveSessions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token]);

  //Close dialogs when device conflict modal appears
  useEffect(() => {
    if (!token) return;

    // Skip for admin routes
    if (isAdminRoute()) {
      return;
    }

    if (deviceConflictModalOpen) {
      endActiveSessions().catch((error) => {
        console.error("Error ending active sessions:", error);
      });
    }
  }, [deviceConflictModalOpen, token]);

  // Show loader as soon as token appears (login switch) so chat does not mount
  // and hit /pagination before fetchConfig restores focus/domain.
  useLayoutEffect(() => {
    setIsLoading(Boolean(token));
  }, [token]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadAsyncData = async () => {
      try {
        setIsLoading(true);

        const url = new URL(window.location.href);
        const urlDomain = url.searchParams.get("domain");
        const urlFocus = url.searchParams.get("focus");

        // Deep-link params must be in the store before any authenticated API calls
        // so axios can send focus/domain headers (e.g. /config, /get_user).
        if (urlDomain || urlFocus) {
          const { setDomain, setFocus } = useAppStore.getState();
          if (urlDomain) setDomain(urlDomain);
          if (urlFocus) setFocus(urlFocus);
        }

        await fetchConfig();

        if (urlDomain || urlFocus) {
          const { domain: currentDomain, focus: currentFocus } =
            useAppStore.getState();
          await setDomainFocus(
            urlDomain ?? currentDomain,
            urlFocus ?? currentFocus
          );
        }

        await fetchUser();
        await fetchDomainTree();

        if (navigator.onLine) {
          await requestAndSendToken();
          await getBrowserData(appVersion);
          await fetchPreassessment();
        }
      } catch (error) {
        console.error("Failed to load app data:", error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadAsyncData();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const connectionModals = (
    <>
      <ConnectionErrorModal
        open={!!connectionError && !deviceConflictModalOpen}
        message={connectionError || ""}
        onClose={() => setConnectionError(null)}
      />
      <DeviceConflictModal
        open={deviceConflictModalOpen}
        onActivate={handleActivateDevice}
        onCancel={handleCancel}
        isLoading={isActivating}
      />
    </>
  );

  const appShell = (
    <ErrorBoundary>
      {!isLoading && <InstallAppBanner />}
      <ToastContainer />
      <AppRouter />
      <GlobalTtsPlayer />
    </ErrorBoundary>
  );

  return (
    <ThemeProvider theme={theme}>
      <InternetStatusBanner />
      {isLoading && <Loader />}
      {token ? (
        <>
          <div
            aria-hidden={isLoading}
            style={{
              visibility: isLoading ? "hidden" : "visible",
              height: isLoading ? 0 : "auto",
              overflow: isLoading ? "hidden" : "visible",
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            {appShell}
          </div>
          {connectionModals}
        </>
      ) : (
        appShell
      )}
    </ThemeProvider>
  );
}

export default App;
